import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import type { OtpCodeInputHandle } from "Components/Shared/Inputs/OtpCodeInput";
import { useSendPhoneVerificationMutation } from "Api/Mutations/Auth/useSendPhoneVerificationMutation";
import { useVerifyPhoneCodeMutation } from "Api/Mutations/Auth/useVerifyPhoneCodeMutation";
import type { PhoneNumber } from "Infrastructure/Api/Api";
import { getPhoneSchema } from "Schemas/phoneSchema";
import { type TFunction, useResource } from "Translations/Resources";
import { EMPTY_PHONE } from "Utils/PhoneUtils";
import { useLanguage } from "Translations/useLanguage";
import { useResendCooldown } from "Hooks/useResendCooldown";
import {
  RESOURCES,
  RESEND_COOLDOWN_SECONDS,
  OTP_CODE_LENGTH,
  NON_DIGIT_REGEX,
  WHITESPACE_REGEX,
} from "./constants";

type VerificationStage = "phoneEntry" | "codeEntry";

const getPhoneFormSchema = (t: TFunction) =>
  z.object({ phone: getPhoneSchema(t) });

export type PhoneFormData = z.infer<ReturnType<typeof getPhoneFormSchema>>;

type Props = {
  defaultPhone?: PhoneNumber;
  onVerified?: () => void;
};

export const usePhoneVerification = ({ defaultPhone, onVerified }: Props) => {
  const [stage, setStage] = useState<VerificationStage>("phoneEntry");
  const [submittedPhone, setSubmittedPhone] =
    useState<PhoneNumber>(EMPTY_PHONE);
  const [code, setCode] = useState("");
  const otpRef = useRef<OtpCodeInputHandle>(null);
  const { t } = useResource();
  const { currentLanguage } = useLanguage();
  const { secondsLeft, isOnCooldown, startCooldown } = useResendCooldown(
    RESEND_COOLDOWN_SECONDS,
  );

  const { mutate: sendVerification, isPending: isSending } =
    useSendPhoneVerificationMutation({
      onSuccess: () => {
        setCode("");
        otpRef.current?.reset();
        setStage("codeEntry");
        startCooldown();
      },
      onError: (error: unknown) => {
        // biome-ignore lint/suspicious/noExplicitAny: erros have complex and inaccurate types
        const status = (error as any)?.data?.status ?? (error as any)?.status;
        const message = t(
          status === 429 ? RESOURCES.TooManyAttempts : RESOURCES.SmsFailed,
        );
        phoneForm.setError("phone", { message });
        toast.error(message);
      },
    });

  const { mutate: verifyCode, isPending: isVerifying } =
    useVerifyPhoneCodeMutation({
      onSuccess: () => {
        toast.success(t(RESOURCES.VerifySuccess));
        onVerified?.();
      },
      onError: () => {
        toast.error(t(RESOURCES.WrongCode));
        setCode("");
        otpRef.current?.reset();
        setStage("phoneEntry");
      },
    });

  const phoneForm = useForm<PhoneFormData>({
    resolver: zodResolver(getPhoneFormSchema(t)),
    defaultValues: { phone: defaultPhone ?? EMPTY_PHONE },
  });

  const handleSendCode = (data: PhoneFormData) => {
    // The masked input keeps grouping spaces in the national number — the API strips them,
    // but the resend/display state should hold the clean value.
    const phone = {
      prefix: data.phone.prefix,
      number: data.phone.number.replace(WHITESPACE_REGEX, ""),
    };
    setSubmittedPhone(phone);
    sendVerification({ phone, language: currentLanguage });
  };

  const isCodeComplete =
    code.replace(NON_DIGIT_REGEX, "").length === OTP_CODE_LENGTH;

  const handleVerifyCode = () => {
    if (!isCodeComplete) {
      return;
    }
    verifyCode({ code });
  };

  const handleResendCode = () => {
    sendVerification({
      phone: submittedPhone,
      language: currentLanguage,
    });
  };

  const handleBackToPhone = () => {
    setStage("phoneEntry");
    setCode("");
  };

  return {
    stage,
    submittedPhone,
    code,
    setCode,
    otpRef,
    isSending,
    isVerifying,
    isCodeComplete,
    secondsLeft,
    isOnCooldown,
    phoneForm,
    handleSendCode,
    handleVerifyCode,
    handleResendCode,
    handleBackToPhone,
  };
};
